import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { DiscountFormResponse } from './useGetDiscount';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { discountsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Form-shaped payload for updating a discount (matches backend UpdateDiscountBody).
 * All fields optional for partial updates. Deactivate/activate sends only startsAt/endsAt.
 */
export interface UpdateDiscountPayload {
	name?: string;
	discountType?: string;
	amountType?: 'fixed' | 'percentage';
	method?: 'code' | 'automatic';
	code?: string;
	amount?: number;
	appliesTo?: 'all' | 'specific';
	applicableProducts?: string[];
	startsAt?: number;
	endsAt?: number;
	hasEndDate?: boolean;
}

/**
 * API function to update a discount via PATCH
 */
export const updateDiscount = async (
	servicesProviderId: string,
	discountId: string,
	payload: UpdateDiscountPayload,
): Promise<DiscountFormResponse> => {
	const response = await apiClient.patch<DiscountFormResponse>(
		discountsEndpoints.updateDiscount(servicesProviderId, discountId),
		payload,
	);
	return response.data;
};

/**
 * Mutation hook for updating a discount.
 * Invalidates both the discount list and detail queries on success.
 */
export const useUpdateDiscount = () => {
	const queryClient = useQueryClient();
	const servicesProviderId = useCurrentServicesProviderId();

	return useMutation({
		mutationFn: ({ discountId, payload }: { discountId: string; payload: UpdateDiscountPayload }) =>
			updateDiscount(servicesProviderId, discountId, payload),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: queries.discounts._def });
			queryClient.invalidateQueries({
				queryKey: queries.discounts.details(servicesProviderId, variables.discountId).queryKey,
			});
		},
	});
};
